import React, { useMemo, useState, useEffect } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity, Dimensions, Modal, Pressable, ScrollView, Platform, TextInput } from 'react-native';
import colors from '../theme/colors';
import { useTableContext } from '../context/TableContext';
import PasswordModal from '../components/PasswordModal';
const { width, height } = Dimensions.get('window');
const isCompactWidth = width < 600;
const isVerySmallWidth = width < 380;
const listPaddingHorizontal = isCompactWidth ? 12 : 24;

const cardShadowStyles = Platform.OS === 'web'
  ? { boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.12)' }
  : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.1,
      shadowRadius: 2,
      elevation: 2
    };

const formatDateTime = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '-';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};
export default function OpenTablesScreen({ onNavigate, screenParams }) {
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('abertas'); // 'abertas', 'fechadas' ou 'reservas'
  const [alertModal, setAlertModal] = useState({ visible: false, title: '', message: '', onConfirm: null });
  const [confirmModal, setConfirmModal] = useState({ visible: false, title: '', message: '', onConfirm: null, onCancel: null });
  const [inputModal, setInputModal] = useState({ visible: false, title: '', placeholder: '', onConfirm: null, onCancel: null });
  const [inputValue, setInputValue] = useState('');
  const [expandedGarcons, setExpandedGarcons] = useState({});
  const [passwordModal, setPasswordModal] = useState({ visible: false, action: null, title: '', table: null });
  const { isKioskMode = false, kioskFunctions = {} } = screenParams || {};
  const { tables, pauseTable, resumeTable, closeTable, addPartialPayment } = useTableContext();
  // Função para carregar reservas do localStorage
  const loadReservations = () => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = window.localStorage.getItem('reservations');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.warn('Erro ao carregar reservas:', error);
      return [];
    }
  };

  const [reservations, setReservations] = useState(() => loadReservations());

  // Atualizar reservas quando mudar para a aba de reservas
  useEffect(() => {
    if (activeTab === 'reservas') {
      setReservations(loadReservations());
    }
  }, [activeTab]);

  const orderedTables = useMemo(() => {
    if (activeTab === 'abertas') {
      return tables.filter(table => table.status !== 'fechada');
    } else if (activeTab === 'fechadas') {
      return tables.filter(table => table.status === 'fechada');
    } else if (activeTab === 'reservas') {
      return reservations;
    }
    return [];
  }, [tables, activeTab, reservations]);
  const filteredTables = useMemo(() => {
    if (!searchQuery.trim()) return orderedTables;
    
    const query = searchQuery.toLowerCase();
    return orderedTables.filter(item => {
      if (activeTab === 'reservas') {
        // Para reservas, buscar nos campos do cliente
        const client = item.client || {};
        const searchFields = [
          item.table?.mesa?.toString() || '',
          client.name?.toLowerCase() || '',
          client.cpf?.toLowerCase() || '',
          client.phone?.toLowerCase() || '',
          client.address?.toLowerCase() || ''
        ];
        return searchFields.some(field => field.includes(query));
      } else {
        // Para mesas, buscar nos campos normais
        const searchFields = [
          item.id?.toString() || '',
          item.mesa?.toString() || '',
          item.cliente?.toLowerCase() || '',
          item.cpf?.toLowerCase() || '',
          item.email?.toLowerCase() || '',
          item.telefone?.toLowerCase() || ''
        ];
        return searchFields.some(field => field.includes(query));
      }
    });
  }, [orderedTables, searchQuery, activeTab]);
  const isMobile = width < 768;
  const visibleTables = useMemo(() => {
    // Em dispositivos móveis, não limitar o número de tabelas
    return filteredTables;
  }, [filteredTables]);
  const selectedTable = useMemo(() => {
    if (!selectedTableId) return null;
    return tables.find(table => table.id === selectedTableId) || null;
  }, [selectedTableId, tables]);
  const handleBack = () => {
    onNavigate('Login', { isKioskMode, kioskFunctions });
  };
  const handleDetail = (table) => {
    setSelectedTableId(table.id);
    setIsModalVisible(true);
  };
  const handleSellProduct = (table) => {
    onNavigate('SellProduct', { tableId: table.id, isKioskMode, kioskFunctions });
  };
  const handlePauseResume = (table) => {
    const action = table.status === 'pausada' ? 'retomar' : 'pausar';
    setPasswordModal({
      visible: true,
      action,
      title: action === 'pausar' ? 'Pausar Mesa' : 'Retomar Mesa',
      table
    });
  };
  const handleTransfer = (table) => {
    setPasswordModal({
      visible: true,
      action: 'transferir',
      title: 'Transferir Mesa',
      table
    });
  };
  const handlePartialPayment = (table) => {
    setPasswordModal({
      visible: true,
      action: 'pagamentoParcial',
      title: 'Pagamento Parcial',
      table
    });
  };
  const handleCloseTable = (table) => {
    setPasswordModal({
      visible: true,
      action: 'fecharMesa',
      title: 'Fechar Mesa',
      table
    });
  };
  const handlePasswordConfirm = () => {
    const { action, table } = passwordModal;
    
    if (!table) return;
    
    if (action === 'pausar') {
      pauseTable(table.id);
      showAlert('Sucesso', `Mesa ${table.mesa} pausada com sucesso!`);
    } else if (action === 'retomar') {
      resumeTable(table.id);
      showAlert('Sucesso', `Mesa ${table.mesa} retomada com sucesso!`);
    } else if (action === 'transferir') {
      showAlert('Transferir Mesa', `Transferir mesa ${table.mesa} - Funcionalidade em desenvolvimento`);
    } else if (action === 'pagamentoParcial') {
      showInput(
        'Pagamento Parcial',
        'Digite o valor do pagamento parcial',
        (value) => {
          if (value && !isNaN(value) && parseFloat(value) > 0) {
            addPartialPayment(table.id, parseFloat(value));
            showAlert('Sucesso', `Pagamento parcial de R$ ${parseFloat(value).toFixed(2)} adicionado à mesa ${table.mesa}`);
          } else {
            showAlert('Erro', 'Por favor, digite um valor válido maior que zero');
          }
          setInputModal({ visible: false, title: '', placeholder: '', onConfirm: null, onCancel: null });
        }
      );
    } else if (action === 'fecharMesa') {
      const total = table.produtos.reduce((sum, produto) => sum + produto.preco * produto.quantidade, 0);
      const partialPayments = table.pagamentosParciais || [];
      const totalPaid = partialPayments.reduce((sum, payment) => sum + payment.amount, 0);
      const remaining = total - totalPaid;
      
      if (remaining > 0) {
        showConfirm(
          'Fechar Mesa',
          `Mesa ${table.mesa}\nTotal: R$ ${total.toFixed(2)}\nPago: R$ ${totalPaid.toFixed(2)}\nRestante: R$ ${remaining.toFixed(2)}\n\nDeseja fechar a mesa?`,
          () => {
            closeTable(table.id);
            showAlert('Sucesso', `Mesa ${table.mesa} fechada com sucesso!`);
            setConfirmModal({ visible: false, title: '', message: '', onConfirm: null, onCancel: null });
          }
        );
      } else {
        closeTable(table.id);
        showAlert('Sucesso', `Mesa ${table.mesa} fechada com sucesso!`);
      }
    }
    
    setPasswordModal({ visible: false, action: null, title: '', table: null });
  };
  const closeModal = () => {
    setIsModalVisible(false);
    setSelectedTableId(null);
  };
  const showAlert = (title, message, onConfirm = null) => {
    setAlertModal({
      visible: true,
      title,
      message,
      onConfirm: onConfirm || (() => setAlertModal({ visible: false, title: '', message: '', onConfirm: null }))
    });
  };
  const showConfirm = (title, message, onConfirm, onCancel = null) => {
    setConfirmModal({
      visible: true,
      title,
      message,
      onConfirm,
      onCancel: onCancel || (() => setConfirmModal({ visible: false, title: '', message: '', onConfirm: null, onCancel: null }))
    });
  };
  const showInput = (title, placeholder, onConfirm, onCancel = null) => {
    setInputValue('');
    setInputModal({
      visible: true,
      title,
      placeholder,
      onConfirm,
      onCancel: onCancel || (() => setInputModal({ visible: false, title: '', placeholder: '', onConfirm: null, onCancel: null }))
    });
  };
  const toggleGarconDetails = (tableId) => {
    setExpandedGarcons((prev) => ({
      ...prev,
      [tableId]: !prev[tableId]
    }));
  };
  const renderReservation = ({ item }) => {
    const client = item.client || {};
    const table = item.table || {};
    
    const handleSendWhatsAppReservation = () => {
      const reservationText = `
*RESERVA DE MESA - VITOR TABLE MT*

📅 Data da Reserva: ${item.date} às ${item.time}

👤 *Dados do Cliente:*
Nome: ${client.name}
CPF: ${client.cpf}
Telefone: ${client.phone}
Endereço: ${client.address}

🍽️ *Informações da Reserva:*
Mesa: ${table ? `Mesa ${table.mesa}` : 'Não selecionada'}
Quantidade de Pessoas: ${client.people}

---
Reserva realizada via VitorTable MT
      `.trim();
      
      const phoneNumber = '5545991046021';
      const encodedMessage = encodeURIComponent(reservationText);
      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
      
      if (typeof window !== 'undefined') {
        window.open(whatsappUrl, '_blank');
      }
    };
    
    return (
      <View style={[isMobile ? styles.tableCardMobile : styles.tableCard, styles.reservationCard]}>
        <View style={styles.tableHeader}>
          <View>
            <Text style={styles.mesaNumber}>
              {table ? `Mesa ${table.mesa}` : 'Mesa não definida'}
            </Text>
            <Text style={[styles.status, styles.statusReservation]}>
              Reservada
            </Text>
          </View>
          <Text style={styles.tempo}>{item.time}</Text>
        </View>
        <Text style={styles.cliente}>{client.name || 'Sem nome'}</Text>
        <Text style={styles.info}>CPF: {client.cpf || 'Não informado'}</Text>
        <Text style={styles.info}>Telefone: {client.phone || 'Não informado'}</Text>
        <Text style={styles.info}>Endereço: {client.address || 'Não informado'}</Text>
        <Text style={styles.info}>Quantidade de Pessoas: {client.people || 'Não informado'}</Text>
        <Text style={[styles.info, styles.reservationDate]}>
          Data da Reserva: {item.date} às {item.time}
        </Text>
        
        <View style={styles.buttonsContainer}>
          <TouchableOpacity 
            style={[styles.detailButton, styles.whatsappButton]} 
            onPress={handleSendWhatsAppReservation}
          >
            <Text style={styles.buttonText}>💬 WhatsApp</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  const renderTable = ({ item }) => {
    const getCardStyle = () => {
      return [
        isMobile ? styles.tableCardMobile : styles.tableCard,
        item.status === 'fechada' && styles.tableCardClosed
      ];
    };
    const isGarconExpanded = !!expandedGarcons[item.id];
    const garconLogs = item.garconLogs || [];
    return (
      <View style={getCardStyle()}>
      <View style={styles.tableHeader}>
        <View>
          <Text style={styles.mesaNumber}>Mesa {item.mesa}</Text>
          <Text style={[
            styles.status, 
            item.status === 'fechada' && styles.statusClosed,
            item.status === 'pausada' && styles.statusPaused
          ]}>
            {item.status === 'fechada' ? 'Saída' : item.status === 'pausada' ? 'Pausada' : 'Aberta'}
          </Text>
        </View>
        <Text style={styles.tempo}>{item.chegada}</Text>
      </View>
      <Text style={styles.cliente}>{item.cliente}</Text>
      <Text style={styles.emailText}>{item.email}</Text>
      <Text style={styles.info}>{item.telefone}</Text>
      <Text style={styles.info}>CPF: {item.cpf}</Text>
      <Text style={styles.info}>{item.pessoas} pessoa(s)</Text>

      {item.garcons.length > 0 && (
        <>
          <TouchableOpacity
            style={[styles.garconSection, styles.garconSectionButton]}
            onPress={() => toggleGarconDetails(item.id)}
            activeOpacity={0.7}
          >
            <Text style={styles.garconButtonLabel}>Garçons: {item.garcons.join(', ')}</Text>
            <Text style={styles.garconToggleIcon}>{isGarconExpanded ? '▲' : '▼'}</Text>
          </TouchableOpacity>
          {isGarconExpanded && (
            <View style={styles.garconLogsContainer}>
              {garconLogs.length > 0 ? (
                garconLogs.map((log, idx) => (
                  <View
                    key={`${item.id}-${idx}`}
                    style={[
                      styles.garconLogRow,
                      idx === garconLogs.length - 1 && styles.garconLogRowLast
                    ]}
                  >
                    <View style={styles.garconLogHeader}>
                      <Text style={styles.garconLogName}>{log.nome}</Text>
                      <Text style={styles.garconLogTime}>{formatDateTime(log.timestamp)}</Text>
                    </View>
                    {log.produto && (
                      <Text style={styles.garconLogProduct}>
                        {log.produto}
                        {log.quantidade ? ` (${log.quantidade}x)` : ''}
                      </Text>
                    )}
                  </View>
                ))
              ) : (
                <Text style={styles.garconLogEmpty}>Nenhum atendimento registrado.</Text>
              )}
            </View>
          )}
        </>
      )}

      <View style={styles.buttonsContainer}>
        <TouchableOpacity 
          style={[
            styles.detailButton,
            item.status === 'fechada' && styles.detailButtonFullWidth
          ]} 
          onPress={() => handleDetail(item)}
        >
          <Text style={styles.buttonText}>Pré-Conta</Text>
        </TouchableOpacity>
        {item.status !== 'fechada' && (
          <TouchableOpacity style={styles.sellButton} onPress={() => handleSellProduct(item)}>
            <Text style={styles.buttonText}>Vender</Text>
          </TouchableOpacity>
        )}
      </View>
      
      {/* Botões de Ações da Mesa */}
      {item.status !== 'fechada' && (
        <View style={styles.actionButtonsWrapper}>
          <View style={styles.actionButtonsRow}>
            <TouchableOpacity 
              style={[styles.actionButtonHalf, styles.pauseButton]} 
              onPress={() => handlePauseResume(item)}
            >
              <Text style={styles.actionButtonText}>
                {item.status === 'pausada' ? '🟡 Retomar' : '🟡 Pausar'}
              </Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={[styles.actionButtonHalf, styles.transferButton]} 
              onPress={() => handleTransfer(item)}
            >
              <Text style={styles.actionButtonText}>🔵 Transferir</Text>
            </TouchableOpacity>
          </View>
          
          <View style={styles.actionButtonsRow}>
            <TouchableOpacity 
              style={[styles.actionButtonHalf, styles.partialPaymentButton]} 
              onPress={() => handlePartialPayment(item)}
            >
              <Text style={styles.actionButtonText}>🟣 Pag. Parcial</Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={[styles.actionButtonHalf, styles.closeButton]} 
              onPress={() => handleCloseTable(item)}
            >
              <Text style={styles.actionButtonText}>🔴 Fechar Mesa</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );
  };
  
  return (
    <View style={styles.container}>
      <View style={styles.headerWrapper}>
        <View style={styles.header}>
          <TouchableOpacity onPress={handleBack}>
            <Text style={styles.backButton}>{'<'}</Text>
          </TouchableOpacity>
          <Text style={styles.title}>Mesas</Text>
          <View style={{ width: 60 }} />
        </View>
      </View>

      <View style={styles.screenContainer}>
        <View style={styles.tabsContainerWrapper}>
          <View style={styles.tabContainer}>
            <TouchableOpacity
              style={[styles.tab, activeTab === 'abertas' && styles.tabActive]}
              onPress={() => setActiveTab('abertas')}
            >
              <Text style={[styles.tabText, activeTab === 'abertas' && styles.tabTextActive]}>
                Abertas
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.tab, activeTab === 'fechadas' && styles.tabActive]}
              onPress={() => setActiveTab('fechadas')}
            >
              <Text style={[styles.tabText, activeTab === 'fechadas' && styles.tabTextActive]}>
                Fechadas
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.tab, activeTab === 'reservas' && styles.tabActive]}
              onPress={() => setActiveTab('reservas')}
            >
              <Text style={[styles.tabText, activeTab === 'reservas' && styles.tabTextActive]}>
                Reservas
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        <View style={styles.searchContainer}>
          <TextInput
            style={styles.searchInput}
            placeholder={activeTab === 'reservas' ? 'Buscar por Mesa, Nome, CPF, Telefone ou Endereço' : 'Buscar por ID, Mesa, Nome, CPF, Email ou Telefone'}
            placeholderTextColor={colors.muted}
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')} style={styles.clearButton}>
              <Text style={styles.clearButtonText}>✕</Text>
            </TouchableOpacity>
          )}
        </View>

        <View style={styles.scrollWrapper}>
          <FlatList
            data={visibleTables}
            keyExtractor={(item) => item.id || item.table?.id || item.timestamp || Math.random().toString()}
            contentContainerStyle={styles.listContent}
            renderItem={activeTab === 'reservas' ? renderReservation : renderTable}
            ListEmptyComponent={() => (
              <View style={styles.emptyState}>
                <Text style={styles.emptyStateText}>
                  {activeTab === 'reservas' ? 'Nenhuma reserva encontrada' : 'Nenhuma mesa encontrada'}
                </Text>
              </View>
            )}
          />
        </View>
      </View>

      <Modal
        visible={isModalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={closeModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Detalhes da Mesa {selectedTable?.mesa}</Text>
              <Pressable onPress={closeModal}>
                <Text style={styles.modalClose}>✕</Text>
              </Pressable>
            </View>
            <ScrollView contentContainerStyle={styles.modalContent}>
              {selectedTable && (
                <>
                  <View style={styles.modalSection}>
                    <Text style={styles.modalSectionTitle}>Cliente</Text>
                    <Text style={styles.modalText}>{selectedTable.cliente}</Text>
                    <Text style={styles.modalEmailText}>{selectedTable.email}</Text>
                    <Text style={styles.modalText}>{selectedTable.telefone}</Text>
                    <Text style={styles.modalText}>CPF: {selectedTable.cpf}</Text>
                    <Text style={styles.modalText}>{selectedTable.pessoas} pessoa(s)</Text>
                  </View>
                  <View style={styles.modalSection}>
                    <Text style={styles.modalSectionTitle}>Horários</Text>
                    <Text style={styles.modalTimeText}>Chegada: {selectedTable.chegada}</Text>
                    <Text style={styles.modalText}>Status: {selectedTable.status === 'fechada' ? 'Fechada' : 'Aberta'}</Text>
                    {selectedTable.saida && <Text style={styles.modalTimeText}>Saída: {selectedTable.saida}</Text>}
                  </View>
                  <View style={styles.modalSection}>
                    <Text style={styles.modalSectionTitle}>Produtos</Text>
                    {selectedTable.produtos.map((produto, idx) => (
                      <Text key={idx} style={styles.modalText}>
                        {produto.nome} ({produto.quantidade}x) - R$ {(produto.preco * produto.quantidade).toFixed(2)}
                      </Text>
                    ))}
                    <View style={styles.modalTotalRow}>
                      <Text style={styles.modalTotalLabel}>Total:</Text>
                      <Text style={styles.modalTotalValue}>
                        R$ {
                          selectedTable.produtos
                            .reduce((sum, produto) => sum + produto.preco * produto.quantidade, 0)
                            .toFixed(2)
                        }
                      </Text>
                    </View>
                  </View>
                  <View style={styles.modalSection}>
                    <Text style={styles.modalSectionTitle}>Garçons</Text>
                    <Text style={styles.modalText}>{selectedTable.garcons.join(', ')}</Text>
                  </View>
                </>
              )}
            </ScrollView>
            <Pressable style={styles.modalButton} onPress={closeModal}>
              <Text style={styles.modalButtonText}>Fechar</Text>
            </Pressable>
          </View>
        </View>
      </Modal>
      {/* Modal de Alerta */}
      <Modal
        visible={alertModal.visible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setAlertModal({ visible: false, title: '', message: '', onConfirm: null })}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.alertContainer}>
            <Text style={styles.alertTitle}>{alertModal.title}</Text>
            <Text style={styles.alertMessage}>{alertModal.message}</Text>
            <TouchableOpacity style={styles.alertButton} onPress={alertModal.onConfirm}>
              <Text style={styles.alertButtonText}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
      {/* Modal de Confirmação */}
      <Modal
        visible={confirmModal.visible}
        transparent={true}
        animationType="fade"
        onRequestClose={confirmModal.onCancel}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.confirmContainer}>
            <Text style={styles.confirmTitle}>{confirmModal.title}</Text>
            <Text style={styles.confirmMessage}>{confirmModal.message}</Text>
            <View style={styles.confirmButtons}>
              <TouchableOpacity style={styles.cancelButton} onPress={confirmModal.onCancel}>
                <Text style={styles.cancelButtonText}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.confirmButton} onPress={confirmModal.onConfirm}>
                <Text style={styles.confirmButtonText}>Confirmar</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
      {/* Modal de Input */}
      <Modal
        visible={inputModal.visible}
        transparent={true}
        animationType="fade"
        onRequestClose={inputModal.onCancel}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.inputContainer}>
            <Text style={styles.inputTitle}>{inputModal.title}</Text>
            <TextInput
              style={styles.inputField}
              placeholder={inputModal.placeholder}
              placeholderTextColor={colors.muted}
              value={inputValue}
              onChangeText={setInputValue}
              keyboardType="numeric"
              autoFocus={true}
            />
            <View style={styles.inputButtons}>
              <TouchableOpacity style={styles.cancelButton} onPress={inputModal.onCancel}>
                <Text style={styles.cancelButtonText}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.confirmButton} onPress={() => inputModal.onConfirm(inputValue)}>
                <Text style={styles.confirmButtonText}>Confirmar</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
      {/* Modal de Senha */}
      <PasswordModal
        visible={passwordModal.visible}
        title={passwordModal.title}
        onClose={() => setPasswordModal({ visible: false, action: null, title: '', table: null })}
        onConfirm={handlePasswordConfirm}
      />
    </View>
  );
}
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    ...(Platform.OS === 'web' && {
      maxWidth: width > 1400 ? 1400 : width > 1200 ? 1200 : '100%',
      alignSelf: 'center',
      width: '100%'
    })
  },
  screenContainer: {
    flex: 1,
    backgroundColor: colors.background,
    paddingHorizontal: width > 1024 ? 48 : 16,
    paddingTop: 24,
    paddingBottom: 24
  },
  headerWrapper: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: colors.border
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
    width: '100%',
    maxWidth: width > 1400 ? 1400 : width > 1200 ? 1200 : '100%',
    alignSelf: 'center'
  },
  tabsContainerWrapper: {
    width: '100%',
    backgroundColor: colors.background,
    borderBottomWidth: 1,
    borderBottomColor: colors.border
  },
  tabContainer: {
    flexDirection: 'row',
    backgroundColor: colors.background,
    paddingHorizontal: 16,
    paddingVertical: 12,
    width: '100%',
    maxWidth: width > 1400 ? 1400 : width > 1200 ? 1200 : '100%',
    alignSelf: 'center',
    flexWrap: 'nowrap'
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent'
  },
  tabActive: {
    borderBottomColor: colors.primary
  },
  tabText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.muted
  },
  tabTextActive: {
    color: colors.primary
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: colors.background,
    borderBottomWidth: 1,
    borderBottomColor: colors.border
  },
  searchInput: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 13,
    color: colors.text
  },
  clearButton: {
    marginLeft: 8,
    padding: 8,
    justifyContent: 'center',
    alignItems: 'center'
  },
  clearButtonText: {
    fontSize: 18,
    color: colors.muted,
    fontWeight: '600'
  },
  backButton: {
    fontSize: 24,
    color: colors.accent,
    fontWeight: '600'
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.text,
    flex: 1,
    textAlign: 'center'
  },
  scrollWrapper: {
    flex: 1,
    width: '100%',
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web' && {
      overflow: 'hidden',
      height: height - 200,
      maxHeight: height - 200,
      boxShadow: 'none',
      borderWidth: 0,
      borderColor: 'transparent',
      borderStyle: 'none'
    })
  },
  scrollableContainer: {
    flex: 1,
    width: '100%',
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web' && {
      overflowY: 'scroll',
      overflowX: 'hidden',
      height: '100%',
      WebkitOverflowScrolling: 'touch',
      paddingRight: 0,
      marginRight: 0,
      marginLeft: 0,
      marginTop: 0,
      marginBottom: 0,
      borderWidth: 0,
      backgroundColor: 'transparent',
      boxShadow: 'none',
      borderTopWidth: 0,
      borderBottomWidth: 0,
      borderLeftWidth: 0,
      borderRightWidth: 0,
      borderRadius: 0,
      borderColor: 'transparent',
      borderStyle: 'none'
    })
  },
  gridContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    paddingTop: 12,
    paddingBottom: 80,
    paddingLeft: 12,
    paddingRight: 24,
    backgroundColor: 'transparent'
  },
  listContainer: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: 16,
    paddingBottom: 80,
    paddingLeft: 16,
    paddingRight: 16,
    gap: 16,
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web' && {
      minHeight: height - 180
    })
  },
  listContent: {
    paddingBottom: 24,
    flexGrow: 1,
    gap: 16,
    ...(Platform.OS === 'web' && {
      display: 'grid',
      gridTemplateColumns: width > 1400 ? 'repeat(6, 1fr)' : width > 1100 ? 'repeat(5, 1fr)' : width > 900 ? 'repeat(4, 1fr)' : width > 700 ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
      columnGap: 16,
      rowGap: 16
    })
  },
  tableCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    ...cardShadowStyles,
    ...(Platform.OS === 'web' && {
      width: '100%'
    })
  },
  tableCardMobile: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 14,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    ...cardShadowStyles
  },
  tableCardGrid: {
    flex: 1,
    marginRight: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: colors.border,
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
    ...cardShadowStyles,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 200
  },
  tableCardGridMobile: {
    flex: 1,
    marginRight: 0,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: colors.border,
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
    ...cardShadowStyles,
    display: 'flex',
    flexDirection: 'column'
  },
  tableCardClosed: {
    backgroundColor: '#FFE5E5',
    borderLeftColor: '#DC2626'
  },
  tableHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 10
  },
  mesaNumber: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.text
  },
  status: {
    fontSize: 11,
    color: colors.primary,
    fontWeight: '600',
    marginTop: 4
  },
  statusClosed: {
    color: '#DC2626'
  },
  statusPaused: {
    color: '#F59E0B'
  },
  statusReservation: {
    color: '#8B5CF6'
  },
  reservationCard: {
    borderLeftWidth: 4,
    borderLeftColor: '#8B5CF6'
  },
  reservationDate: {
    fontWeight: '600',
    color: colors.text,
    marginTop: 8
  },
  whatsappButton: {
    backgroundColor: '#25D366',
    width: '100%'
  },
  tempo: {
    fontSize: 13,
    color: colors.time,
    fontWeight: '700'
  },
  cliente: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 6
  },
  emailText: {
    fontSize: 13,
    color: colors.email,
    marginBottom: 4,
    fontWeight: '500'
  },
  info: {
    fontSize: 13,
    color: colors.muted,
    marginBottom: 4
  },
  pessoas: {
    fontSize: 12,
    color: colors.muted
  },
  produtosSection: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: colors.border
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 6
  },
  produtoItem: {
    fontSize: 13,
    color: colors.muted,
    marginBottom: 4
  },
  garconSection: {
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: colors.border
  },
  garconSectionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  garconButtonLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.text
  },
  garconToggleIcon: {
    fontSize: 12,
    color: colors.muted,
    fontWeight: '700'
  },
  garconLogsContainer: {
    marginTop: 8,
    padding: 10,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    gap: 8
  },
  garconLogRow: {
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    paddingBottom: 6,
    marginBottom: 6
  },
  garconLogRowLast: {
    borderBottomWidth: 0,
    paddingBottom: 0,
    marginBottom: 0
  },
  garconLogHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  garconLogName: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.primary
  },
  garconLogTime: {
    fontSize: 12,
    color: colors.muted,
    fontWeight: '500'
  },
  garconLogProduct: {
    fontSize: 12,
    color: colors.text,
    marginTop: 4
  },
  garconLogEmpty: {
    fontSize: 12,
    color: colors.muted,
    textAlign: 'center'
  },
  buttonsContainer: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 10,
    width: '100%'
  },
  detailButton: {
    flex: 1,
    backgroundColor: colors.accent,
    paddingVertical: 8,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 36
  },
  detailButtonFullWidth: {
    width: '100%'
  },
  sellButton: {
    flex: 1,
    backgroundColor: colors.primary,
    paddingVertical: 8,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 36
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center'
  },
  actionButtonsWrapper: {
    marginTop: 10,
    width: '100%',
    gap: 6
  },
  actionButtonsRow: {
    flexDirection: 'column',
    gap: 8,
    marginBottom: 6
  },
  actionButtonHalf: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 38
  },
  pauseButton: {
    backgroundColor: '#F59E0B'
  },
  transferButton: {
    backgroundColor: '#3B82F6'
  },
  partialPaymentButton: {
    backgroundColor: '#8B5CF6'
  },
  closeButton: {
    backgroundColor: '#EF4444'
  },
  actionButtonText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '600',
    textAlign: 'center'
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16
  },
  modalContainer: {
    width: '100%',
    maxWidth: 480,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    overflow: 'hidden'
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.text
  },
  modalClose: {
    fontSize: 18,
    color: colors.accent,
    fontWeight: '700'
  },
  modalContent: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    gap: 16
  },
  modalSection: {
    backgroundColor: colors.background,
    borderRadius: 12,
    padding: 16,
    borderLeftWidth: 3,
    borderLeftColor: colors.primary
  },
  modalSectionTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.primary,
    marginBottom: 8
  },
  modalText: {
    fontSize: 13,
    color: colors.text,
    marginBottom: 4
  },
  modalEmailText: {
    fontSize: 13,
    color: colors.email,
    marginBottom: 4,
    fontWeight: '600'
  },
  modalTimeText: {
    fontSize: 13,
    color: colors.time,
    marginBottom: 4,
    fontWeight: '600'
  },
  modalTotalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: colors.border
  },
  modalTotalLabel: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.text
  },
  modalTotalValue: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.primary
  },
  modalButton: {
    backgroundColor: colors.primary,
    paddingVertical: 14,
    alignItems: 'center'
  },
  modalButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '700'
  },
  // Estilos dos novos modais
  alertContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    margin: 20,
    maxWidth: 300,
    alignItems: 'center'
  },
  alertTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.text,
    marginBottom: 12,
    textAlign: 'center'
  },
  alertMessage: {
    fontSize: 14,
    color: colors.text,
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 20
  },
  alertButton: {
    backgroundColor: colors.primary,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    minWidth: 80
  },
  alertButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center'
  },
  confirmContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    margin: 20,
    maxWidth: 350
  },
  confirmTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.text,
    marginBottom: 12,
    textAlign: 'center'
  },
  confirmMessage: {
    fontSize: 14,
    color: colors.text,
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 20
  },
  confirmButtons: {
    flexDirection: 'row',
    gap: 12
  },
  cancelButton: {
    flex: 1,
    backgroundColor: colors.muted,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center'
  },
  cancelButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600'
  },
  confirmButton: {
    flex: 1,
    backgroundColor: colors.primary,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center'
  },
  confirmButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600'
  },
  inputContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    margin: 20,
    maxWidth: 300
  },
  inputTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.text,
    marginBottom: 16,
    textAlign: 'center'
  },
  inputField: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    marginBottom: 20
  },
  emptyContainer: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center'
  },
  emptyText: {
    fontSize: 16,
    color: colors.muted,
    textAlign: 'center'
  },
  inputButtons: {
    flexDirection: 'row',
    gap: 12
  }
});
